// ---------------------
// Custom Dropdown
// Example Usage:

// <custom-dropdown position="right">
//   <span slot="label">Select an option</span>
//   <div slot="content">
//     <ul>
//       <li>item 1</li>
//       <li>item 2</li>
//       <li>item 3</li>
//     </ul>
//   </div>
// </custom-dropdown>


const defineCustomElements = () => {
    class CustomDropdown extends HTMLElement {
      static get observedAttributes() {
        return ['position', 'trigger', 'hide-arrow'];
      }
  
      constructor() {
        super();
        const shadow = this.attachShadow({ mode: "open" });
  
        // Create template
        const template = document.createElement("template");
        template.innerHTML = `
          <style>
            .container {
              position: relative;
              display: inline-block;
            }
  
            .label-container {
              display: flex;
              align-items: center;
              cursor: pointer;
            }
  
            .arrow {
              transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            }
  
            .dropdown-content {
              position: absolute;
              z-index: 999;
              width: max-content;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              overflow: hidden;
              opacity: 0;
              transition: 
                opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
              visibility: hidden;
            }
  
            /* Position variations */
            .dropdown-content[data-position="bottom"] {
              top: calc(100% + 1rem);
              left: 50%;
              transform: translateY(10px) translateX(-50%);
            }
  
            .dropdown-content[data-position="bottom-start"] {
              top: calc(100% + 1rem);
              left: 0;
              transform: translateY(10px);
            }
  
            .dropdown-content[data-position="bottom-end"] {
              top: calc(100% + 1rem);
              right: 0;
              transform: translateY(10px);
            }
            
            .dropdown-content[data-position="bottom-left"] {
              top: calc(100% + 1rem);
              left: 0;
              transform: translateY(10px);
            }
  
            .dropdown-content[data-position="bottom-right"] {
              top: calc(100% + 1rem);
              right: 0;
              transform: translateY(10px);
            }
  
            .dropdown-content[data-position="top"] {
              bottom: calc(100% + 1rem);
              left: 50%;
              transform: translateY(-10px) translateX(-50%);
            }
  
            .dropdown-content[data-position="top-start"] {
              bottom: calc(100% + 1rem);
              left: 0;
              transform: translateY(-10px);
            }
  
            .dropdown-content[data-position="top-end"] {
              bottom: calc(100% + 1rem);
              right: 0;
              transform: translateY(-10px);
            }
            
            .dropdown-content[data-position="top-left"] {
              bottom: calc(100% + 1rem);
              left: 0;
              transform: translateY(-10px);
            }
  
            .dropdown-content[data-position="top-right"] {
              bottom: calc(100% + 1rem);
              right: 0;
              transform: translateY(-10px);
            }
  
            .dropdown-content[data-position="left"] {
              top: 50%;
              right: calc(100% + 1rem);
              transform: translateY(-50%) translateX(-10px);
            }
            
            .dropdown-content[data-position="left-top"] {
              top: 0;
              right: calc(100% + 1rem);
              transform: translateX(-10px);
            }
            
            .dropdown-content[data-position="left-bottom"] {
              bottom: 0;
              right: calc(100% + 1rem);
              transform: translateX(-10px);
            }
  
            .dropdown-content[data-position="right"] {
              top: 50%;
              left: calc(100% + 1rem);
              transform: translateY(-50%) translateX(10px);
            }
            
            .dropdown-content[data-position="right-top"] {
              top: 0;
              left: calc(100% + 1rem);
              transform: translateX(10px);
            }
            
            .dropdown-content[data-position="right-bottom"] {
              bottom: 0;
              left: calc(100% + 1rem);
              transform: translateX(10px);
            }
  
            /* Modified hover states to use a class instead */
            .container.active .dropdown-content[data-position="bottom"],
            .container.active .dropdown-content[data-position="bottom-start"],
            .container.active .dropdown-content[data-position="bottom-end"],
            .container.active .dropdown-content[data-position="bottom-left"],
            .container.active .dropdown-content[data-position="bottom-right"] {
              opacity: 1;
              visibility: visible;
            }
            
            .container.active .dropdown-content[data-position="bottom"] {
              transform: translateY(0) translateX(-50%);
            }
  
            .container.active .dropdown-content[data-position="bottom-start"],
            .container.active .dropdown-content[data-position="bottom-left"] {
              transform: translateY(0);
            }
  
            .container.active .dropdown-content[data-position="bottom-end"],
            .container.active .dropdown-content[data-position="bottom-right"] {
              transform: translateY(0);
            }
  
            .container.active .dropdown-content[data-position="top"],
            .container.active .dropdown-content[data-position="top-start"],
            .container.active .dropdown-content[data-position="top-end"],
            .container.active .dropdown-content[data-position="top-left"],
            .container.active .dropdown-content[data-position="top-right"] {
              opacity: 1;
              visibility: visible;
            }
            
            .container.active .dropdown-content[data-position="top"] {
              transform: translateY(0) translateX(-50%);
            }
  
            .container.active .dropdown-content[data-position="top-start"],
            .container.active .dropdown-content[data-position="top-left"] {
              transform: translateY(0);
            }
  
            .container.active .dropdown-content[data-position="top-end"],
            .container.active .dropdown-content[data-position="top-right"] {
              transform: translateY(0);
            }
  
            .container.active .dropdown-content[data-position="left"],
            .container.active .dropdown-content[data-position="left-top"],
            .container.active .dropdown-content[data-position="left-bottom"] {
              opacity: 1;
              visibility: visible;
            }
            
            .container.active .dropdown-content[data-position="left"] {
              transform: translateY(-50%) translateX(0);
            }
            
            .container.active .dropdown-content[data-position="left-top"],
            .container.active .dropdown-content[data-position="left-bottom"] {
              transform: translateX(0);
            }
  
            .container.active .dropdown-content[data-position="right"],
            .container.active .dropdown-content[data-position="right-top"],
            .container.active .dropdown-content[data-position="right-bottom"] {
              opacity: 1;
              visibility: visible;
            }
            
            .container.active .dropdown-content[data-position="right"] {
              transform: translateY(-50%) translateX(0);
            }
            
            .container.active .dropdown-content[data-position="right-top"],
            .container.active .dropdown-content[data-position="right-bottom"] {
              transform: translateX(0);
            }
  
            .container.active .arrow {
              transform: rotate(180deg);
            }
  
            /* Replace the hover-specific styles with position-aware ones */
            .container[data-trigger="hover"]:hover .dropdown-content {
              opacity: 1;
              visibility: visible;
            }
            
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom"] {
              transform: translateY(0) translateX(-50%);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-start"],
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-left"] {
              transform: translateY(0);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-end"],
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="bottom-right"] {
              transform: translateY(0);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="top"] {
              transform: translateY(0) translateX(-50%);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-start"],
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-left"] {
              transform: translateY(0);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-end"],
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="top-right"] {
              transform: translateY(0);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="left"] {
              transform: translateY(-50%) translateX(0);
            }
            
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="left-top"],
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="left-bottom"] {
              transform: translateX(0);
            }
  
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="right"] {
              transform: translateY(-50%) translateX(0);
            }
            
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="right-top"],
            .container[data-trigger="hover"]:hover .dropdown-content[data-position="right-bottom"] {
              transform: translateX(0);
            }
  
            .container[data-trigger="hover"]:hover .arrow {
              transform: rotate(180deg);
            }
  
            /* Buffer zones for different positions */
            .container::before {
              content: '';
              position: absolute;
              background: transparent;
            }
  
            /* Bottom positions buffer */
            .container:has(.dropdown-content[data-position^="bottom"])::before {
              top: 100%;
              left: 0;
              right: 0;
              height: 1rem;
            }
  
            /* Top positions buffer */
            .container:has(.dropdown-content[data-position^="top"])::before {
              bottom: 100%;
              left: 0;
              right: 0;
              height: 1rem;
            }
  
            /* Left position buffer */
            .container:has(.dropdown-content[data-position^="left"])::before {
              top: 0;
              bottom: 0;
              right: 100%;
              width: 1rem;
            }
  
            /* Right position buffer */
            .container:has(.dropdown-content[data-position^="right"])::before {
              top: 0;
              bottom: 0;
              left: 100%;
              width: 1rem;
            }
            
            /* Hide arrow when attribute is set */
            .arrow.hidden {
              display: none;
            }
          </style>
  
          <div class="container">
            <div class="label-container">
              <slot name="label"></slot>
              <svg class="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </div>
            <div class="dropdown-content">
              <slot name="content"></slot>
            </div>
          </div>
        `;
  
        shadow.appendChild(template.content.cloneNode(true));
        this.dropdownContent = shadow.querySelector('.dropdown-content');
        this.container = shadow.querySelector('.container');
        this.labelContainer = shadow.querySelector('.label-container');
        this.arrow = shadow.querySelector('.arrow');
      }
  
      connectedCallback() {
        // Set default position if none provided
        if (!this.hasAttribute('position')) {
          this.setAttribute('position', 'bottom');
        }
        // Set default trigger if none provided
        if (!this.hasAttribute('trigger')) {
          this.setAttribute('trigger', 'hover');
        }
        
        this.updatePosition();
        this.setupTrigger();
        this.updateArrowVisibility();
      }
  
      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'position' && oldValue !== newValue) {
          this.updatePosition();
        }
        if (name === 'trigger' && oldValue !== newValue) {
          this.setupTrigger();
        }
        if (name === 'hide-arrow' && oldValue !== newValue) {
          this.updateArrowVisibility();
        }
      }
  
      updatePosition() {
        const position = this.getAttribute('position') || 'bottom';
        this.dropdownContent.setAttribute('data-position', position);
      }
      
      updateArrowVisibility() {
        if (this.hasAttribute('hide-arrow')) {
          this.arrow.classList.add('hidden');
        } else {
          this.arrow.classList.remove('hidden');
        }
      }
  
      setupTrigger() {
        const trigger = this.getAttribute('trigger') || 'hover';
        this.container.setAttribute('data-trigger', trigger);
        
        // Remove existing listeners
        this.labelContainer.removeEventListener('click', this.toggleDropdown);
        
        // Setup click handler if needed
        if (trigger === 'click') {
          this.toggleDropdown = () => {
            this.container.classList.toggle('active');
          };
          
          this.labelContainer.addEventListener('click', this.toggleDropdown);
          
          // Close dropdown when clicking outside
          document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) {
              this.container.classList.remove('active');
            }
          });
        }
      }
    }
  
    customElements.define("custom-dropdown", CustomDropdown);
  };
  
  defineCustomElements();
// ---------------------
